import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";

const baseUrl = import.meta.env.VITE_BASE_API || "";

const CONFIRMATION_MESSAGE =
  "You have successfully unsubscribed from Cloud Job Manager newsletters.";

export default function NewsletterUnsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading | confirm | success | error
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("Cloud Job Manager");
  const [categoryLabel, setCategoryLabel] = useState("these emails");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage(
        "This unsubscribe link is invalid. Missing token. If you received this in an email, please use the full link from the message."
      );
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${baseUrl}/v1/newsletter/unsubscribe`, {
          params: { token },
        });
        if (cancelled) return;
        if (res?.data?.statusCode === 200 && res?.data?.data) {
          setEmail(res.data.data.email || res.data.data.maskedEmail || "");
          setCompanyName(res.data.data.companyName || "Cloud Job Manager");
          setCategoryLabel(res.data.data.categoryLabel || "these emails");
          setStatus("confirm");
        } else {
          setStatus("error");
          setErrorMessage(
            res?.data?.message ||
              "This unsubscribe link is invalid or has expired."
          );
        }
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage(
          err?.response?.data?.message ||
            "This unsubscribe link is invalid or has expired. If you still wish to unsubscribe, please contact the sender or use the preference in your account settings."
        );
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirm = async () => {
    if (!token || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${baseUrl}/v1/newsletter/unsubscribe`, {
        token,
      });
      if (res?.data?.statusCode === 200) {
        setSuccessMessage(res?.data?.message || CONFIRMATION_MESSAGE);
        setStatus("success");
      } else {
        setErrorMessage(res?.data?.message || "Unable to complete unsubscribe.");
        setStatus("error");
      }
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          "Unable to complete unsubscribe. Please try again later."
      );
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const theme = {
    primary: "#0f2b46",
    accent: "var(--amber)",
    textPrimary: "#0f2b46",
    textSecondary: "#666",
    bgPage: "#f8f9fa",
    cardBorder: "1px solid #e0e0e0",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: theme.bgPage,
        p: 2,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 560,
          width: "100%",
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          borderRadius: "12px",
          border: theme.cardBorder,
          boxShadow: "var(--shadow-lg)",
        }}
      >
        {status === "loading" && (
          <>
            <CircularProgress
              size={48}
              sx={{ my: 3, color: theme.accent }}
            />
            <Typography variant="body1" sx={{ color: theme.textSecondary, fontSize: "1.1rem" }}>
              Loading…
            </Typography>
          </>
        )}

        {status === "confirm" && (
          <>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: theme.textPrimary,
                fontSize: "1.5rem",
                paddingBottom: "10px",
                borderBottom: `2px solid ${theme.accent}`,
                display: "inline-block",
              }}
            >
              Unsubscribe from {categoryLabel}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 2.5, color: theme.textSecondary, lineHeight: 1.7, fontSize: "1.05rem" }}
            >
              You are unsubscribing for{" "}
              <Box component="span" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                {email}
              </Box>{" "}
              from{" "}
              <Box component="span" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                {companyName}
              </Box>{" "}
              {categoryLabel.toLowerCase()}.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 4, color: theme.textSecondary, lineHeight: 1.7, fontSize: "1.05rem" }}
            >
              Click below to confirm. You will no longer receive {categoryLabel.toLowerCase()} from this company.
            </Typography>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting}
              fullWidth
              sx={{
                textTransform: "none",
                py: 1.6,
                fontSize: "1.05rem",
                fontWeight: 600,
                borderRadius: "8px",
                backgroundColor: theme.accent,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#d97a3a",
                  boxShadow: "var(--shadow-xl)",
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Confirm unsubscribe"
              )}
            </Button>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 72, color: "#2e7d32", mb: 2, display: "block", mx: "auto" }}
            />
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.textPrimary,
                fontSize: "1.5rem",
                paddingBottom: "10px",
                borderBottom: `2px solid ${theme.accent}`,
                display: "inline-block",
              }}
            >
              Unsubscribe successful
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.textSecondary,
                lineHeight: 1.7,
                fontSize: "1.1rem",
                maxWidth: "90%",
                mx: "auto",
                mb: 3,
              }}
            >
              {successMessage || CONFIRMATION_MESSAGE}
            </Typography>
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: "1px solid #e8e8e8",
                textAlign: "left",
                maxWidth: "90%",
                mx: "auto",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.textSecondary,
                  lineHeight: 1.75,
                  fontSize: "1.05rem",
                }}
              >
                To receive these emails again, please contact the company.
              </Typography>
            </Box>
          </>
        )}

        {status === "error" && (
          <>
            <ErrorOutlineIcon
              sx={{ fontSize: 72, color: "#d32f2f", mb: 2, display: "block", mx: "auto" }}
            />
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.textPrimary,
                fontSize: "1.5rem",
                paddingBottom: "10px",
                borderBottom: `2px solid ${theme.accent}`,
                display: "inline-block",
              }}
            >
              Invalid or expired link
            </Typography>
            <Alert
              severity="info"
              sx={{
                textAlign: "left",
                mt: 2,
                borderRadius: "8px",
                border: `1px solid ${theme.primary}30`,
                "& .MuiAlert-message": { color: theme.textSecondary, fontSize: "1.05rem", lineHeight: 1.6 },
              }}
            >
              {errorMessage}
            </Alert>
          </>
        )}
      </Paper>
    </Box>
  );
}
